import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { TrashIcon } from '@heroicons/react/24/outline'
import { formatPrice } from '@/utils/formatters'
import NumberFormatterInput from '@/components/ui/Input/NumberFormatter'

import { 
  addRentLine, 
  removeRentLine, 
  addChargeLine, 
  removeChargeLine,
} from './quittancesSlice'

export default ({ quittance }) => {
  const dispatch = useDispatch()
  const [newRentLine, setNewRentLine] = useState({ description: '', amount: '' })
  const [newChargeLine, setNewChargeLine] = useState({ description: '', amount: '' })
  
  // Initialiser les lignes supplémentaires si elles n'existent pas
  const additionalLines = quittance.additional_lines || { rent_lines: [], charges_lines: [] }
  const rentLines = additionalLines.rent_lines || []
  const chargesLines = additionalLines.charges_lines || []
  
  // Gérer l'ajout d'une ligne de loyer
  const handleAddRentLine = (e) => {
    // e.preventDefault()
    // e.stopPropagation()
    if (!newRentLine.description || !newRentLine.montant) return
    
    dispatch(addRentLine({
      quittanceId: quittance.id,
      description: newRentLine.description,
      montant: parseFloat(newRentLine.montant)
    }))
    
    setNewRentLine({ description: '', montant: '' })
  }
  
  // Gérer la suppression d'une ligne de loyer
  const handleRemoveRentLine = (index) => {
    dispatch(removeRentLine({
      quittanceId: quittance.id,
      index
    }))
  }
  
  // Gérer l'ajout d'une ligne de charge
  const handleAddChargeLine = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!newChargeLine.description || !newChargeLine.montant) return
    
    dispatch(addChargeLine({
      quittanceId: quittance.id,
      description: newChargeLine.description,
      montant: parseFloat(newChargeLine.montant)
    }))
    
    setNewChargeLine({ description: '', montant: '' })
  }
  
  // Gérer la suppression d'une ligne de charge
  const handleRemoveChargeLine = (index) => {
    dispatch(removeChargeLine({
      quittanceId: quittance.id,
      index
    }))
  }
  
  // Gérer la sauvegarde des modifications
  const handleSave = async () => {
    try {
      await dispatch(updateRent({
        id: quittance.id,
        additionalLines: {
          rent_lines: rentLines,
          charges_lines: chargesLines
        }
      })).unwrap()
      
      // Afficher un message de succès
      alert('Quittance mise à jour avec succès')
    } catch (error) {
      // Afficher un message d'erreur
      alert(`Erreur lors de la mise à jour de la quittance: ${error}`)
    } finally {
      // setLoading(false)
    }
  }
  
  return (
    <div>
      <div className="grid grid-cols-2">
      {/* Section des lignes de loyer */}
      <fieldset className="fieldset p-2">
        <legend className="fieldset-legend">Lignes supplémentaires pour le loyer</legend>
        
        {/* Liste des lignes de loyer existantes */}
        {rentLines.length > 0 ? (
          <div className="overflow-x-auto mb-4">
            <table className="table table-compact w-full">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Montant</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rentLines.map((line, index) => (
                  <tr key={`rent-line-${index}`}>
                    <td>{line.description}</td>
                    <td>{formatPrice(line.montant)}</td>
                    <td>
                      <button 
                        onClick={(e) => handleRemoveRentLine(index)}
                        className="btn btn-xs btn-ghost btn-square p-1 hover:bg-primary-400"
                        title={`Supprimer le document`} aria-label={`Supprimer le document`}
                      >
                        <TrashIcon/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500 mb-4">Aucune ligne supplémentaire pour le loyer</p>
        )}
        
      </fieldset>

      
      {/* Section des lignes de charges */}
      <fieldset className="fieldset p-2">
        <legend className="fieldset-legend">Lignes supplémentaires pour les charges</legend>
        
        {/* Liste des lignes de charges existantes */}
        {chargesLines.length > 0 ? (
          <div className="overflow-x-auto mb-4">
            <table className="table table-compact w-full">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Montant</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {chargesLines.map((line, index) => (
                  <tr key={`charge-line-${index}`}>
                    <td>{line.description}</td>
                    <td>{formatPrice(line.montant)}</td>
                    <td>
                      <button 
                        className="btn btn-xs btn-ghost btn-square p-1 hover:bg-primary-400"
                        title={`Supprimer le document`} aria-label={`Supprimer le document`}
                        onClick={() => handleRemoveChargeLine(index)}
                      >
                        <TrashIcon/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500 mb-4">Aucune ligne supplémentaire pour les charges</p>
        )}
        
        
      </fieldset>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {/* Formulaire pour ajouter une nouvelle ligne de loyer */}
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            placeholder="Description"
            className="input input-bordered flex-1"
            value={newRentLine.description}
            onChange={(e) => setNewRentLine({ ...newRentLine, description: e.target.value })}
          />
          <label className="input currency-input w-48">
            <NumberFormatterInput name="new_rent_line_montant"
              placeholder="Montant"
              value={newRentLine.montant}
              onChange={(value) => setNewRentLine({ ...newRentLine, montant: value })}
            />
            <span className="label">€</span>
          </label>
          <button 
            className="btn btn-primary"
            onClick={handleAddRentLine}
            disabled={!newRentLine.description || !newRentLine.montant}
          >
            Ajouter
          </button>
        </div>

        {/* Formulaire pour ajouter une nouvelle ligne de charge */}
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            placeholder="Description"
            className="input input-bordered flex-1"
            value={newChargeLine.description}
            onChange={(e) => setNewChargeLine({ ...newChargeLine, description: e.target.value })}
          />
          <label className="input currency-input w-48">
            <NumberFormatterInput name="new_charge_line_montant"
              placeholder="Montant"
              value={newChargeLine.montant}
              onChange={(value) => setNewChargeLine({ ...newChargeLine, montant: value })}
            />
            <span className="label">€</span>
          </label>
          <button 
            className="btn btn-primary"
            onClick={handleAddChargeLine}
            disabled={!newChargeLine.description || !newChargeLine.montant}
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  )
}